/**
 * Tap’N Win — ONE super-admin control page: links, tonight's game, prizes and
 * odds, screen wording, branding and the night's clean-up. Super admin only
 * (the backend enforces it on every call, not just the route).
 *
 * Branding is NOT rebuilt here — the section reads and writes the same Gift
 * Generator setups and voucher colours that Voucher Settings uses.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Coins, Gamepad2, Loader2, Lock, Radio, Users } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { Block } from '../../components/goalgame/admin/Block';
import { GoalLinksBlock } from '../../components/goalgame/admin/GoalLinksBlock';
import { GoalTonightBlock } from '../../components/goalgame/admin/GoalTonightBlock';
import { GoalPrizesBlock } from '../../components/goalgame/admin/GoalPrizesBlock';
import { GoalWordingBlock } from '../../components/goalgame/admin/GoalWordingBlock';
import { GoalBrandingBlock } from '../../components/goalgame/admin/GoalBrandingBlock';
import { GoalDesignBlock } from '../../components/goalgame/admin/GoalDesignBlock';
import { GoalBoardDesignBlock } from '../../components/goalgame/admin/GoalBoardDesignBlock';
import { GoalPlayersBlock } from '../../components/goalgame/admin/GoalPlayersBlock';
import { GoalSponsorsBlock } from '../../components/goalgame/admin/GoalSponsorsBlock';
import { GoalAutomationBlock } from '../../components/goalgame/admin/GoalAutomationBlock';
import { GoalOrgBlock } from '../../components/goalgame/admin/GoalOrgBlock';
import { GoalPromotionBlock } from '../../components/goalgame/admin/GoalPromotionBlock';
import { GoalFanPreview } from '../../components/goalgame/admin/GoalFanPreview';
import { GoalMaintenanceBlock } from '../../components/goalgame/admin/GoalMaintenanceBlock';
import { GoalDemoExperienceBar } from '../../components/goalgame/admin/GoalDemoExperienceBar';
import { GoalPlayPlacesBar } from '../../components/goalgame/admin/GoalPlayPlacesBar';
import { GoalReminderDefaultsBar } from '../../components/goalgame/admin/GoalReminderDefaultsBar';
import { GoalSportImageBlock } from '../../components/goalgame/admin/GoalSportImageBlock';
import { GoalTableExtrasBar } from '../../components/goalgame/admin/GoalTableExtrasBar';
import { GoalBrandLinkBar } from '../../components/goalgame/admin/GoalBrandLinkBar';
import { GoalGiftGuardBar } from '../../components/goalgame/admin/GoalGiftGuardBar';

/** No tick, no page. A direct link lands here instead of a blank screen; the
 *  tick itself is in the permissions screen's "Tap’N Win" group. */
const NoGoalAccess = () => {
    const { t } = useTranslation();
    return (
        <div className="p-8 text-center" data-testid="goal-no-access">
            <Lock className="w-10 h-10 text-muted-foreground/40 mx-auto mb-3" />
            <p className="text-lg font-medium">{t('goalAccess.title')}</p>
            <p className="text-sm text-muted-foreground">{t('goalAccess.body')}</p>
        </div>
    );
};

function GoalGameControlPage() {
    const { t } = useTranslation();
    const { isSuperAdmin } = useAuth();
    const [orgs, setOrgs] = useState([]);
    const [available, setAvailable] = useState([]);
    const [demoOrgId, setDemoOrgId] = useState('');
    const [orgId, setOrgId] = useState(null);
    // Which promotion the page is EDITING. null = this organization's original
    // promotion, which is the only one most venues will ever have.
    const [promotionId, setPromotionId] = useState(null);
    const [cfg, setCfg] = useState(null);
    const [error, setError] = useState('');
    const [open, setOpen] = useState({ links: true });

    // Unsaved drafts, lifted so the phone preview can draw them. The setters
    // are stable, so handing them to a block costs that block no renders.
    const [designDraft, setDesignDraft] = useState(null);
    const [brandDraft, setBrandDraft] = useState(null);
    const [copyDraft, setCopyDraft] = useState(null);

    const toggle = (id) => setOpen((p) => ({ ...p, [id]: !p[id] }));

    // Shared by the first load and by enable/disable, so a new organization
    // appears in the dropdown at once and a removed one drops out of it.
    const loadOrgs = useCallback(async (selectId) => {
        const res = await api.get('/goal-game/admin/orgs');
        const list = res.data.organizations || [];
        setOrgs(list);
        setAvailable(res.data.available || []);
        setDemoOrgId(res.data.demo_organization_id || '');
        setOrgId((prev) => {
            const wanted = selectId || prev;
            if (wanted && list.some((o) => o.id === wanted)) return wanted;
            return list[0]?.id || null;
        });
        if (list.length === 0) setError('Tap’N Win is not switched on for any organization.');
        return list;
    }, []);

    useEffect(() => {
        (async () => {
            try {
                // A link from Voucher Settings, Game Results or Live Game View
                // names the venue it came from: open on that one.
                const wanted = new URLSearchParams(window.location.search)
                    .get('organization_id') || undefined;
                await loadOrgs(wanted);
            } catch (e) {
                setError(e.response?.status === 403 ? 'Super admin only.' : 'Could not load Tap’N Win.');
            }
        })();
    }, [loadOrgs]);

    const reload = useCallback(async () => {
        if (!orgId) return;
        const res = await api.get('/goal-game/admin/config', {
            params: { organization_id: orgId, promotion_id: promotionId || undefined },
        });
        setCfg(res.data);
    }, [orgId, promotionId]);

    useEffect(() => {
        if (!orgId) return;
        setDesignDraft(null); setBrandDraft(null); setCopyDraft(null);
        reload().catch(() => setError('Could not load this organization.'));
    }, [orgId, promotionId, reload]);

    const saveConfig = useCallback(async (patch) => {
        try {
            const res = await api.put('/goal-game/admin/config', {
                organization_id: orgId, promotion_id: promotionId || null, ...patch });
            setCfg(res.data);
            toast.success('Saved');
        } catch (_e) {
            toast.error('Could not save');
        }
    }, [orgId, promotionId]);

    if (error) {
        return <div className="p-8 text-sm text-slate-500" data-testid="gg-control-error">{error}</div>;
    }
    if (!cfg) {
        return <div className="p-8"><Loader2 className="animate-spin text-slate-400" size={22} /></div>;
    }

    return (
        <div className="max-w-6xl mx-auto p-4 sm:p-6 space-y-4" data-testid="gg-control-page">
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <h1 className="text-2xl font-bold">Tap’N Win</h1>
                    <p className="text-sm text-slate-500" data-testid="gg-control-card">
                        Table card {cfg.card_id}
                        {(cfg.promotions || []).length > 1 && ` · ${cfg.promotion_name}`}
                        {cfg.league && cfg.league !== 'nhl' && ` · ${(cfg.leagues || []).find((l) => l.id === cfg.league)?.label || ''}`}
                    </p>
                </div>
                <label className="block min-w-[220px]">
                    <span className="block text-xs font-medium text-slate-500 mb-1">Organization</span>
                    <select
                        value={orgId || ''}
                        onChange={(e) => { setCfg(null); setPromotionId(null); setOrgId(e.target.value); }}
                        data-testid="gg-org-select"
                        className="w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm"
                    >
                        {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                    </select>
                </label>
                {(cfg.promotions || []).length > 1 && (
                    <label className="block min-w-[220px]">
                        <span className="block text-xs font-medium text-slate-500 mb-1">Promotion</span>
                        <select
                            value={promotionId || ''}
                            onChange={(e) => { setCfg(null); setPromotionId(e.target.value || null); }}
                            data-testid="gg-promotion-select"
                            className="w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm"
                        >
                            {(cfg.promotions || []).map((p) => (
                                <option key={p.promotion_id || 'main'} value={p.promotion_id || ''}>
                                    {p.name}{p.is_live ? ' — live' : ''}
                                </option>
                            ))}
                        </select>
                    </label>
                )}
            </div>

            {/* WHERE PLAYERS MAY PLAY — two switches, at the very top. */}
            <GoalPlayPlacesBar organizationId={orgId} />

            {/* THE ONE TAPELLIGENCE WEBSITE ADDRESS — what the small logo at the
                foot of every player page opens. Super admin only. */}
            {isSuperAdmin() && <GoalBrandLinkBar />}

            {/* THE REMINDER EVERY NEW PLAYER STARTS WITH — on, 30 minutes
                before, by email. One tap each way. */}
            <GoalReminderDefaultsBar organizationId={orgId} />

            {/* EVERY PLAYER AT THE TABLE, A GIFT FOR THE NEXT VISIT. */}
            <GoalTableExtrasBar organizationId={orgId} />

            {/* GIFT GUARD — money-value prize cards activated by text, and the
                venue's reservation button. */}
            <GoalGiftGuardBar organizationId={orgId} isSuperAdmin={isSuperAdmin()} />

            {/* DEMO ONLY — the two-way switch for the whole demo, always at the
                top and never inside a collapsed block. */}
            <GoalDemoExperienceBar organizationId={orgId} />

            {/* The Patron Points set-up lives on its own page; from the page
                where the game is run it is one tap away. Super admin only, the
                same as the page itself and the menu entry. */}
            {isSuperAdmin() && (
                <Link to={`/patron-points?organization_id=${orgId || ''}`} data-testid="gg-patron-points-link"
                      className="inline-flex items-center gap-2 text-sm font-medium text-amber-600 hover:underline">
                    <Coins size={15} />
                    {t('goalGame.patronPointsSetup')}
                </Link>
            )}

            {/* THE PLAYERS LIST — this business's own players and the gifts
                they have won here. One tap from this page. */}
            <Link to={`/tapnwin-players?organization_id=${orgId || ''}`} data-testid="gg-players-link"
                  className="inline-flex items-center gap-2 text-sm font-medium text-purple-600 hover:underline ml-4">
                <Users size={15} />
                {t('goalGame.playersList.title')}
            </Link>

            {/* Game Results — every play from this venue, in one place. */}
            <Link to={`/game-results?organization_id=${orgId || ''}`} data-testid="gg-game-results-link"
                  className="inline-flex items-center gap-2 text-sm font-medium text-blue-600 hover:underline ml-4">
                <Gamepad2 size={15} />
                {t('gameResults.title', 'Game Results')}
            </Link>

            {/* Live Game View — tonight, right now. */}
            <Link to={`/live-game-view?organization_id=${orgId || ''}`} data-testid="gg-live-game-link"
                  className="inline-flex items-center gap-2 text-sm font-medium text-emerald-600 hover:underline ml-4">
                <Radio size={15} />
                {t('liveGame.title', 'Live Game View')}
            </Link>

            <Block id="players" title="Players"
                   hint="Venue badges, one player per phone, and each venue's public join link"
                   open={!!open.players} onToggle={() => toggle('players')}>
                <GoalPlayersBlock organizationId={orgId} />
            </Block>

            <Block id="sportImages" title="Sport pictures"
                   hint="The picture beside “Choose your star”, one per sport"
                   open={!!open.sportImages} onToggle={() => toggle('sportImages')}>
                <GoalSportImageBlock orgId={orgId} />
            </Block>

            <Block id="sponsors" title="At-home sponsors"
                   hint="Who pays for tonight's at-home prizes. In-bar play is not affected."
                   open={!!open.sponsors} onToggle={() => toggle('sponsors')}>
                <GoalSponsorsBlock organizationId={orgId} />
            </Block>

            <Block id="links" title="Links" hint="Fan, operator, iPad demo and the two trigger links"
                   open={!!open.links} onToggle={() => toggle('links')}>
                <GoalLinksBlock cfg={cfg} onReload={reload} />
            </Block>

            <Block id="tonight" title="Tonight's game" hint="Title and message on the fan screens, and the goal window"
                   open={!!open.tonight} onToggle={() => toggle('tonight')}>
                <GoalTonightBlock cfg={cfg} onSave={saveConfig} />
            </Block>

            <Block id="prizes" title="Prizes and odds"
                   hint={`${(cfg.prizes || []).filter((p) => p.active).length} active · 1 in ${cfg.odds_one_in}`}
                   open={!!open.prizes} onToggle={() => toggle('prizes')}>
                <GoalPrizesBlock cfg={cfg} orgId={orgId} promotionId={promotionId}
                                 onSave={saveConfig} onReload={reload} />
            </Block>

            <Block id="wording" title="Screen wording" hint="EN and FR, with a reset per language"
                   open={!!open.wording} onToggle={() => toggle('wording')}>
                <GoalWordingBlock cfg={cfg} onSave={saveConfig} onDraft={setCopyDraft} />
            </Block>

            {/* Branding and Design sit beside the phone, so a colour, a font or
                a logo can be judged on the screen it will land on. */}
            <div className="grid md:grid-cols-[minmax(0,1fr)_320px] gap-4 items-start">
                <div className="space-y-4 min-w-0">
                    <Block id="branding" title="Branding"
                           hint={`Gift setup in use: ${cfg.resolved_setup_name || '—'}`}
                           open={!!open.branding} onToggle={() => toggle('branding')}>
                        <GoalBrandingBlock cfg={cfg} orgId={orgId} promotionId={promotionId} onSave={saveConfig} onReload={reload}
                                           onDraft={setBrandDraft} />
                    </Block>

                    <Block id="design" title="Design"
                           hint="Theme, fonts, sizes, wallpaper darkening and the win celebration"
                           open={!!open.design} onToggle={() => toggle('design')}>
                        <GoalDesignBlock cfg={cfg} orgId={orgId} promotionId={promotionId} onReload={reload} onDraft={setDesignDraft} />
                    </Block>

                    {/* The Live players board has its own look: logos,
                        wallpaper, watermark, colours, fonts and the pinned
                        announcement's banner. */}
                    <Block id="boardDesign" title="Live players board design"
                           hint="Logos, wallpaper, watermark, colours, fonts and the announcement banner"
                           open={!!open.boardDesign} onToggle={() => toggle('boardDesign')}>
                        <GoalBoardDesignBlock cfg={cfg} orgId={orgId} promotionId={promotionId}
                                              onReload={reload} />
                    </Block>
                </div>

                {/* Sticky from tablet up, offset BELOW the portal's bars: the
                    narrow layout has a fixed 64px bar plus the 56px top nav, so
                    the panel starts under both and the "Before the goal / Win"
                    buttons are always reachable without scrolling. */}
                {/* The panel keeps its own height: nothing is clipped and the
                    page scroll is the only scroll. The state buttons inside it
                    are pinned below the portal's bars, so they can always be
                    tapped while the Branding and Design boxes are being used. */}
                <div className="self-start" data-testid="gg-preview-panel">
                    <GoalFanPreview cfg={cfg} design={designDraft} brandDraft={brandDraft}
                                    copyDraft={copyDraft} />
                </div>
            </div>

            <Block id="organizations" title="Organizations"
                   hint="Switch Tap’N Win on for another sponsor, or off again"
                   open={!!open.organizations} onToggle={() => toggle('organizations')}>
                <GoalOrgBlock
                    available={available} enabled={orgs} orgId={orgId} demoOrgId={demoOrgId}
                    onChanged={async (selectId) => { await loadOrgs(selectId); }}
                />
            </Block>

            <Block id="promotions" title="Promotions"
                   hint={`${(cfg.promotions || []).length} in this venue · editing ${cfg.promotion_name || 'Main promotion'}${cfg.star?.enabled ? ' · star pick on' : ''}`}
                   open={!!open.promotions} onToggle={() => toggle('promotions')}>
                <GoalPromotionBlock cfg={cfg} orgId={orgId} promotionId={promotionId}
                                    onPick={(pid) => { setCfg(null); setPromotionId(pid || null); }}
                                    onReload={reload} onSave={saveConfig} />
            </Block>

            <Block id="automation" title="Live-game automation"
                   hint="Pick tonight's game and let goals open the rounds"
                   open={!!open.automation} onToggle={() => toggle('automation')}>
                <GoalAutomationBlock orgId={orgId} promotionId={promotionId} onReload={reload} />
            </Block>

            <Block id="tables" title="Tables and history" hint="Clear the scratches so a phone can play again"
                   open={!!open.tables} onToggle={() => toggle('tables')}>
                <GoalMaintenanceBlock cfg={cfg} orgId={orgId} onReload={reload} />
            </Block>
        </div>
    );
}

export default function GoalGameControl() {
    const { hasPermission } = useAuth();
    if (!hasPermission('manage_goal_game')) return <NoGoalAccess />;
    return <GoalGameControlPage />;
}
